import axios from 'axios'
import store from '../store'
import router from '../router'
import enums from '../utils/enums'
import { getPortalApiBaseUrl } from '../utils/config/portalApiBaseUrl'

const api = axios.create({
  baseURL: getPortalApiBaseUrl(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

let sessionUnauthorizedUiSuppressedUntilMs = 0
const SESSION_UNAUTHORIZED_ALERT_COOLDOWN_MS = 3000
const PORTAL_PWA_SESSION_KEY = 'portalAccessMode'

export function getPortalAccessMode() {
  if (typeof window === 'undefined') return 'unknown'
  if (window.navigator?.standalone === true) return 'ios_standalone'
  try {
    const urlMode = new URLSearchParams(window.location.search).get('portalAccessMode')
    if (urlMode === 'pwa_standalone') {
      window.sessionStorage?.setItem(PORTAL_PWA_SESSION_KEY, 'pwa_standalone')
      return 'pwa_standalone'
    }
    if (window.sessionStorage?.getItem(PORTAL_PWA_SESSION_KEY) === 'pwa_standalone') {
      return 'pwa_standalone'
    }
  } catch {
    // Si sessionStorage no esta disponible, se usa la deteccion nativa.
  }
  const installedDisplayModes = [
    'standalone',
    'fullscreen',
    'minimal-ui',
    'window-controls-overlay',
  ]
  if (installedDisplayModes.some((mode) => window.matchMedia?.(`(display-mode: ${mode})`)?.matches)) {
    return 'pwa_standalone'
  }
  return 'browser'
}

export function extractClientErrorMessage(data) {
  if (data == null) return 'Solicitud no válida.'
  if (typeof data === 'string') return data
  if (typeof data === 'object') {
    if (data.message) return String(data.message)
    if (data.title) return String(data.title)
    if (data.detail) return String(data.detail)
    if (data.error) {
      const e = data.error
      return Array.isArray(e) ? String(e[0]) : String(e)
    }
    if (data.errors && typeof data.errors === 'object') {
      const first = Object.values(data.errors)[0]
      return Array.isArray(first) ? String(first[0]) : String(first)
    }
  }
  return 'No se pudo completar la operación.'
}

api.interceptors.request.use(
  (config) => {
    // Si hay sesión de invitado activa, su token temporal tiene prioridad
    // sobre cualquier token regular que pueda haber quedado en localStorage.
    // Caso contrario, se usa el token normal (usuario logueado).
    const guest = store.state?.guestSession
    const guestToken = String(guest?.accessToken ?? '').trim()
    if (guest?.activo === true && guestToken) {
      config.headers.Authorization = `Bearer ${guestToken}`
    } else {
      const token = localStorage.getItem('token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    }
    config.headers.Accept = 'application/json'
    config.headers['X-Portal-Access-Mode'] = getPortalAccessMode()
    return config
  },
  (error) => Promise.reject(error),
)

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error
    const skipGlobalErrorAlert = error.config?.skipGlobalErrorAlert === true

    if (!response) {
      if (!skipGlobalErrorAlert) {
        store.commit('alerts/setAlertMessage', {
          type: 'error',
          message: `${enums.messages.UNEXPECTED_ERROR}${error.message ? ` (${error.message})` : ''}`,
        })
      }
      return Promise.reject(error)
    }

    const status = response.status

    if (status === 401) {
      const url = String(response.config?.url ?? '')
      const wrongCurrentPassword =
        url.includes('/auth/cambiar-password') &&
        response.data?.errorCode === 'credenciales_invalidas'

      if (!wrongCurrentPassword) {
        const guest = store.state?.guestSession
        const eraSesionInvitada =
          guest?.activo === true && String(guest?.accessToken ?? '').trim() !== ''
        localStorage.removeItem('token')
        // Si había sesión de invitado activa (token temporal expirado/no aceptado),
        // también la limpiamos para evitar reintentos con el mismo token vencido.
        if (store.state?.guestSession?.activo === true) {
          store.commit('guestSession/resetGuestSession')
        }
        const now = Date.now()
        if (now >= sessionUnauthorizedUiSuppressedUntilMs) {
          sessionUnauthorizedUiSuppressedUntilMs =
            now + SESSION_UNAUTHORIZED_ALERT_COOLDOWN_MS
          const path = String(router.currentRoute.value.path ?? '')
          const invitadoPerdioSesionEnNuevoTurno =
            eraSesionInvitada && path.startsWith('/appointments/nuevo')
          if (path !== '/login') {
            if (invitadoPerdioSesionEnNuevoTurno) {
              router.push({
                name: 'login',
                query: { nuevoTurno: 'sesion-expirada' },
              })
            } else {
              store.commit('alerts/setAlertMessage', {
                type: 'warning',
                message:
                  'Sesión expirada o no autorizada. Inicie sesión nuevamente.',
              })
              router.push('/login')
            }
          }
        }
      }
      return Promise.reject(error)
    }

    if (status === 403) {
      if (!skipGlobalErrorAlert) {
        store.commit('alerts/setAlertMessage', {
          type: 'warning',
          message: enums.messages.ACCESS_DENIED,
        })
      }
      return Promise.reject(error)
    }

    if (status >= 400 && status < 500) {
      if (!skipGlobalErrorAlert) {
        store.commit('alerts/setAlertMessage', {
          type: 'warning',
          message: extractClientErrorMessage(response.data),
        })
      }
      return Promise.reject(error)
    }

    if (status >= 500) {
      if (!skipGlobalErrorAlert) {
        const fromBody = extractClientErrorMessage(response.data)
        const generic =
          fromBody === 'No se pudo completar la operación.' || fromBody === 'Solicitud no válida.'
        store.commit('alerts/setAlertMessage', {
          type: 'error',
          message: !generic ? fromBody : enums.messages.SYSTEM_ERROR,
          detail: response.data,
        })
      }
      return Promise.reject(error)
    }

    return Promise.reject(error)
  },
)

export default api
